import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  CheckIcon,
  CpuChipIcon,
  HeartIcon,
  PhoneIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { trackCustom, trackStandard, getUtmParams } from '../../lib/pixel';
import { trackEvent, trackPostHog } from '../../lib/analytics';
import { useScrollDepth } from '../../lib/useScrollDepth';

const PAGE = 'inscriptions-2026-2027';

type CycleValue = 'Maternelle' | 'Primaire' | 'Collège' | 'Lycée';

type FormState = {
  parentName: string;
  phone: string;
  niveau: CycleValue | '';
  email: string;
};

const NIVEAU_CHIPS: { value: CycleValue; label: string; classes: string }[] = [
  { value: 'Maternelle', label: 'Maternelle', classes: 'PS · MS · GS' },
  { value: 'Primaire',   label: 'Primaire',   classes: 'CP → CE6' },
  { value: 'Collège',    label: 'Collège',    classes: '1AC → 3AC' },
  { value: 'Lycée',      label: 'Lycée',      classes: 'TC → 2bac' },
];

const ASSET_BASE = '/inscriptions-2026-2027';

const trustItems = [
  ['29 ans', "d'engagement à Hay El Oulfa"],
  ['4 campus', 'dédiés, un par cycle'],
  ['Maternelle → Bac', 'sans rupture'],
  ['4 programmes', 'Neurogames · Lumi · IA · Soft Skills'],
];

const programmes = [
  {
    title: 'Neurogames',
    tagline: 'Le mouvement au service du cerveau',
    audience: 'Maternelle 3-6 ans',
    color: '#F59E0B',
    icon: SparklesIcon,
    text: "Avant d'apprendre à lire, le cerveau apprend à apprendre. Neurogames prépare la coordination, l'équilibre, la concentration et l'attention.",
    chips: ['Fondations motrices', 'Intégration sensorielle', 'Préparation cognitive'],
    link: '/programme-neurogames',
  },
  {
    title: 'Lumi',
    tagline: 'Le 1er tuteur IA marocain',
    audience: 'Primaire CP → CE6',
    color: '#007CBA',
    icon: CpuChipIcon,
    text: "Lumi parle, explique les leçons et pose des questions. Il accompagne l'enfant à la maison avec une méthode socratique.",
    chips: ['Arabe', 'Français', 'Anglais', 'Mathématiques', 'Sciences'],
    link: '/revision',
  },
  {
    title: 'Programme IA',
    tagline: 'Préparer nos enfants au monde de demain',
    audience: 'Primaire CP → CE6',
    color: '#7C3AED',
    icon: CpuChipIcon,
    text: "Une initiation à l'intelligence artificielle, sécurisée et encadrée. Chaque interaction se fait sous supervision de l'enseignant.",
    chips: ['Gemini', 'Canva Éducation', 'Google for Education', 'Makeblock'],
    safety: ['Aucune information personnelle', "L'adulte d'abord", "L'esprit critique avant tout"],
    link: '/programme-ia',
  },
  {
    title: 'Soft Skills',
    tagline: "Les compétences qui préparent l'avenir",
    audience: 'Primaire CP → CE6',
    color: '#0D9488',
    icon: HeartIcon,
    text: "Un programme structuré sur six ans pour gérer ses émotions, résoudre les conflits, travailler en équipe et développer son leadership naturel.",
    chips: ['Fondations', 'Développement', 'Leadership'],
    link: '/programme-soft-skills',
  },
];

const niveaux = [
  {
    value: 'Maternelle' as CycleValue,
    title: 'Maternelle',
    classes: 'PS · MS · GS',
    image: 'maternelle.webp',
    color: '#F59E0B',
    icon: '🌱',
    text: "Programme Neurogames dès la PS pour préparer le cerveau à la lecture et à l'écriture. Premiers repères, premier vocabulaire, mêmes adultes de référence.",
  },
  {
    value: 'Primaire' as CycleValue,
    title: 'Primaire',
    classes: 'CP · CE1 · CE2 · CM1 · CM2 · CE6',
    image: 'primaire.webp',
    color: '#0D9488',
    icon: '📚',
    text: "Six années pour poser les fondations : Lumi à la maison, Programme IA en classe et Soft Skills sur tout le primaire.",
  },
  {
    value: 'Collège' as CycleValue,
    title: 'Collège',
    classes: '1ère AC · 2ème AC · 3ème AC',
    image: 'college.webp',
    color: '#7C3AED',
    icon: '🧠',
    text: "L'âge où l'élève devient autonome. Lumi pour les révisions, préparation au brevet et suivi rapproché parents-équipe pédagogique.",
  },
  {
    value: 'Lycée' as CycleValue,
    title: 'Lycée',
    classes: 'Tronc commun · 1ère bac · 2ème bac',
    image: 'lycee.webp',
    color: '#007CBA',
    icon: '🎓',
    text: "Tronc commun jusqu'au baccalauréat, filières scientifiques et économiques, révisions intensives et préparation post-bac.",
  },
];

const faqs = [
  ['Faut-il payer pour la visite ?', 'Non. La visite privée est gratuite et sans engagement.'],
  ['Mon enfant doit-il venir avec moi ?', "C'est mieux pour la maternelle. Pour le primaire et au-delà, c'est facultatif : beaucoup de parents préfèrent venir seuls la première fois."],
  ['Combien coûte la scolarité ?', "Les frais sont communiqués lors de la visite privée. Le bot WhatsApp peut aussi répondre à certaines questions pratiques avant votre rendez-vous."],
  ['Quels documents apporter ?', "Aucun pour la visite. Si vous décidez d'inscrire votre enfant, on vous donnera la liste à ce moment-là."],
  ['Et si je réfléchis à plusieurs écoles ?', "C'est normal et sain. La visite privée sert exactement à comparer ce que vous voyez ici avec ce que vous avez vu ailleurs."],
  ['Vous proposez le transport scolaire ?', "Oui, selon les quartiers desservis. Notre équipe vous confirme les zones disponibles et les tarifs lors de la visite."],
];

const cleanPhone = (phone: string) => phone.replace(/[\s\-().]/g, '');

const validatePhone = (phone: string) => /^(?:\+212|0)[5-7]\d{8}$/.test(cleanPhone(phone));

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const scrollToForm = () => {
  document.getElementById('formulaire')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Container = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`mx-auto w-full max-w-[1200px] px-6 ${className}`}>{children}</div>
);

const Squiggle = ({ className = '' }: { className?: string }) => (
  <svg className={`absolute -bottom-2 left-0 h-3 w-full ${className}`} viewBox="0 0 200 12" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 8 Q50 0 100 8 T200 8" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const SectionIntro = ({
  eyebrow,
  title,
  children,
  centered = true,
}: {
  eyebrow: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  centered?: boolean;
}) => (
  <div className={`${centered ? 'mx-auto text-center' : ''} mb-12 max-w-3xl`}>
    <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.10em] text-teal-700">{eyebrow}</p>
    <h2 className="text-[clamp(28px,3.6vw,40px)] font-bold leading-tight tracking-tight text-gray-900">{title}</h2>
    {children && <div className="mt-6 text-[17px] leading-7 text-gray-600">{children}</div>}
  </div>
);

const InscriptionPage = () => {
  const [form, setForm] = useState<FormState>({ parentName: '', phone: '', niveau: '', email: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [openFaq, setOpenFaq] = useState(0);
  const [stickyVisible, setStickyVisible] = useState(false);
  const formStarted = useRef(false);
  const parentNameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useScrollDepth(PAGE);

  useEffect(() => {
    document.title = 'Visiter le Groupe Scolaire Sanaa — RDV Rentrée 2026-2027 | GSS';
    trackStandard('ViewContent', {
      content_name: 'inscriptions-2026-2027',
      content_category: 'funnel',
    });
    trackPostHog('view_landing', { page: PAGE });
  }, []);

  const handleFocus = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackPostHog('form_started', { page: PAGE });
  };

  const validateField = (key: keyof FormState, value: string): string | undefined => {
    if (key === 'parentName' && value.trim().length > 0 && value.trim().length < 2) {
      return 'Au moins 2 caractères';
    }
    if (key === 'phone' && value.trim().length > 0 && !validatePhone(value)) {
      return 'Numéro marocain invalide (ex. 06 12 34 56 78)';
    }
    if (key === 'email' && value.trim().length > 0 && !validateEmail(value)) {
      return 'Format email invalide';
    }
    return undefined;
  };

  const handleBlur = (fieldName: keyof FormState) => () => {
    const value = form[fieldName];
    const stringValue = typeof value === 'string' ? value : String(value ?? '');
    const inlineError = validateField(fieldName, stringValue);
    if (inlineError) {
      setErrors((prev) => ({ ...prev, [fieldName]: inlineError }));
    }
    trackPostHog('form_field_blur', {
      page: PAGE,
      field_name: fieldName,
      has_value: stringValue.trim().length > 0,
      has_error: Boolean(inlineError || errors[fieldName]),
    });
  };

  const focusNext = (ref: RefObject<HTMLInputElement>) => {
    setTimeout(() => ref.current?.focus(), 50);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const trackCta = (label: string) =>
    trackEvent('cta_clicked', { cta_label: label, page: PAGE });

  const trackOutbound = (target: string) =>
    trackEvent('outbound_click', { target, page: PAGE });

  useEffect(() => {
    const onScroll = () => {
      const formEl = document.getElementById('formulaire');
      if (!formEl) return;
      const rect = formEl.getBoundingClientRect();
      setStickyVisible(window.scrollY > 620 && !(rect.top < window.innerHeight - 100 && rect.bottom > 100));
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const selectNiveau = (niveau: CycleValue) => {
    handleFocus();
    setField('niveau', niveau);
    trackCta(`visite-niveau-${niveau.toLowerCase()}`);
    trackPostHog('form_field_blur', {
      page: PAGE,
      field_name: 'niveau',
      has_value: true,
      has_error: false,
    });
    focusNext(parentNameRef);
  };

  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.niveau) next.niveau = 'Sélectionnez un niveau.';
    if (!form.parentName.trim()) next.parentName = 'Votre nom est requis.';
    if (!form.phone.trim()) next.phone = 'Numéro WhatsApp requis.';
    else if (!validatePhone(form.phone)) next.phone = 'Numéro marocain invalide.';
    if (form.email.trim() && !validateEmail(form.email)) next.email = 'Email invalide.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate() || !form.niveau) return;

    setIsSubmitting(true);
    try {
      await axios.post('/api/visite', {
        parent_name: form.parentName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        quartier: 'Non renseigné - visite privée',
        niveau: form.niveau,
        nombre_enfants: 1,
        source: 'Autre',
      });

      trackStandard('Lead', { content_name: 'inscriptions-2026-2027', currency: 'MAD' });
      trackCustom('private_visit_requested', {
        niveau: form.niveau,
        ...getUtmParams(),
      });
      trackPostHog('form_submitted', {
        page: PAGE,
        niveau: form.niveau,
        has_email: Boolean(form.email.trim()),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-700">
      <main>
        <section className="relative overflow-hidden bg-[linear-gradient(135deg,#FBF7F2_0%,#FFFFFF_52%,#F0FAF8_100%)] pt-12">
          <div className="absolute -right-28 -top-32 h-[460px] w-[460px] rounded-full bg-teal-500/15 blur-3xl" />
          <div className="absolute -left-28 top-44 h-80 w-80 rounded-full bg-violet-500/10 blur-3xl" />
          <Container>
            <div className="grid items-center gap-12 py-12 pb-24 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-600/20 bg-teal-600/10 px-4 py-2 text-sm font-semibold text-teal-800">
                  <span className="h-2 w-2 rounded-full bg-teal-600 shadow-[0_0_0_4px_rgba(13,148,136,0.18)]" />
                  Rentrée 2026-2027 · Visite privée sur RDV
                </span>
                <h1 className="max-w-3xl text-[clamp(34px,5vw,58px)] font-bold leading-[1.04] tracking-tight text-gray-950">
                  L'école qui accompagnera votre enfant pendant les{' '}
                  <span className="relative inline-block whitespace-nowrap text-teal-700">
                    15 prochaines années
                    <Squiggle className="text-teal-600/35" />
                  </span>
                </h1>
                <p className="mt-6 max-w-xl text-xl leading-8 text-gray-700">
                  Visite privée sur rendez-vous, en présence de l'équipe pédagogique.
                </p>
                <p className="mt-4 max-w-xl leading-7 text-gray-500">
                  Une seule équipe pédagogique qui connaît votre enfant depuis ses 3 ans, dans 4 campus dédiés à Hay El Oulfa. Pas de test entre la maternelle et le CP. Pas de changement d'école en 1ère AC.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button type="button" onClick={() => { trackCta('hero-visite-privee'); scrollToForm(); }} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-7 py-4 font-semibold text-white shadow-xl shadow-teal-700/20 transition hover:-translate-y-0.5 hover:bg-teal-700">
                    Demander une visite privée
                    <ArrowRightIcon className="h-5 w-5" />
                  </button>
                  <a href="https://wa.me/212781570080" onClick={() => trackOutbound('whatsapp-hero')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-lg border border-green-500/30 bg-green-50 px-5 py-4 font-semibold text-green-700 transition hover:bg-green-100">
                    WhatsApp · Agent IA disponible
                  </a>
                  <Link to="/contact" className="font-semibold text-teal-700 hover:text-teal-800">
                    Nous contacter →
                  </Link>
                </div>
                <p className="mt-4 text-sm text-gray-500">Réponse WhatsApp en moins de 2 heures, du lundi au samedi.</p>
              </motion.div>

              <motion.div className="relative mx-auto w-full max-w-[520px]" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
                <div className="absolute -left-5 -top-5 h-4/5 w-4/5 rounded-[20px] bg-amber-400" />
                <div className="absolute -bottom-5 -right-5 h-2/3 w-2/3 rounded-[20px] bg-teal-600" />
                <div className="relative overflow-hidden rounded-[18px] shadow-2xl shadow-gray-900/25 ring-1 ring-black/5">
                  <img src={`${ASSET_BASE}/home-hero.webp`} alt="Élèves au Groupe Scolaire Sanaa" className="aspect-[4/5] w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950/30 to-transparent" />
                  <div className="absolute right-5 top-5 rounded-full bg-white/95 px-4 py-2 text-xs font-semibold text-teal-800 shadow-lg">Visite privée sur RDV</div>
                </div>
                <div className="absolute -bottom-7 -left-4 flex min-w-[240px] items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-xl">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-teal-600/10 text-teal-700">
                    <UserGroupIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-950">Une équipe, 15 ans</div>
                    <div className="text-xs text-gray-500">De la PS au baccalauréat</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </Container>
        </section>

        <section id="formulaire" className="relative overflow-hidden bg-[linear-gradient(180deg,#fff_0%,#FBF7F2_52%,#F0FAF8_100%)] py-20">
          <div className="absolute -right-28 -top-32 h-[460px] w-[460px] rounded-full bg-teal-500/10 blur-3xl" />
          <Container>
            <div className="relative grid gap-12 lg:grid-cols-[0.85fr_1fr] lg:gap-16">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <SectionIntro eyebrow="Le formulaire" title={<>Réservez votre <span className="relative inline-block text-teal-700">visite privée<Squiggle className="text-teal-600/35" /></span></>} centered={false}>
                  Remplissez ce formulaire, et notre secrétariat vous propose 2 ou 3 créneaux dans les 5 jours qui viennent.
                </SectionIntro>
                <ul className="space-y-3">
                  {['Réponse WhatsApp en moins de 2 heures', '2 ou 3 créneaux proposés dans les 5 jours', "Visite privée avec l'équipe pédagogique", 'Aucun engagement, aucun frais'].map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-gray-700">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-teal-600/10 text-teal-700"><CheckIcon className="h-4 w-4" /></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <form onSubmit={handleSubmit} noValidate className="rounded-[20px] border border-gray-100 bg-white p-6 shadow-2xl shadow-teal-900/10 md:p-9">
                {submitError && <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{submitError}</div>}
                {isSubmitted ? (
                  <div className="py-8 text-center">
                    <div className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-full bg-teal-600/10 text-teal-700">
                      <CheckIcon className="h-10 w-10" />
                    </div>
                    <h3 className="text-3xl font-bold tracking-tight text-gray-950">C'est noté.</h3>
                    <p className="mx-auto mt-3 max-w-md leading-7 text-gray-600">
                      Vous allez recevoir un message WhatsApp dans les minutes qui viennent. Si vous voulez nous joindre directement :
                    </p>
                    <div className="mx-auto mt-6 grid max-w-sm gap-3">
                      <a href="tel:0522908238" onClick={() => trackOutbound('phone-confirm')} className="flex items-center gap-3 rounded-xl border border-gray-100 p-4 text-left text-gray-900">
                        <PhoneIcon className="h-6 w-6 text-teal-700" />
                        <span><strong className="block">0522 90 82 38</strong><span className="text-xs text-gray-500">Secrétariat, lundi-samedi 8h-17h</span></span>
                      </a>
                      <a href="https://wa.me/212781570080" onClick={() => trackOutbound('whatsapp-confirm')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl border border-gray-100 p-4 text-left text-gray-900">
                        <PhoneIcon className="h-6 w-6 text-green-500" />
                        <span><strong className="block">WhatsApp 0781 57 00 80</strong><span className="text-xs text-gray-500">Agent IA disponible 24/7</span></span>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <FormField id="niveau" label="Niveau visé pour votre enfant" error={errors.niveau} required>
                      <select
                        id="niveau"
                        name="niveau"
                        value={form.niveau}
                        onChange={(e) => {
                          const value = e.target.value as CycleValue;
                          if (value) selectNiveau(value);
                        }}
                        onFocus={handleFocus}
                        required
                        className="form-input"
                      >
                        <option value="" disabled>Choisir le niveau de votre enfant…</option>
                        {NIVEAU_CHIPS.map((chip) => (
                          <option key={chip.value} value={chip.value}>
                            {chip.label} ({chip.classes})
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField id="parentName" label="Votre nom" error={errors.parentName} required>
                      <input
                        ref={parentNameRef}
                        id="parentName"
                        name="parentName"
                        value={form.parentName}
                        onChange={(e) => setField('parentName', e.target.value)}
                        onFocus={handleFocus}
                        onBlur={handleBlur('parentName')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            phoneRef.current?.focus();
                          }
                        }}
                        autoComplete="name"
                        enterKeyHint="next"
                        className="form-input"
                        placeholder="Prénom et nom"
                      />
                    </FormField>
                    <FormField id="phone" label="Numéro WhatsApp" helper="On vous répond sous 2h en semaine" error={errors.phone} required>
                      <input
                        ref={phoneRef}
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        value={form.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                        onFocus={handleFocus}
                        onBlur={handleBlur('phone')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            emailRef.current?.focus();
                          }
                        }}
                        autoComplete="tel"
                        enterKeyHint="next"
                        className="form-input"
                        placeholder="0612345678"
                      />
                    </FormField>
                    <FormField id="email" label="Email" error={errors.email} optional>
                      <input
                        ref={emailRef}
                        id="email"
                        name="email"
                        type="email"
                        inputMode="email"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        onFocus={handleFocus}
                        onBlur={handleBlur('email')}
                        autoComplete="email"
                        enterKeyHint="done"
                        className="form-input"
                        placeholder="vous@exemple.com"
                      />
                    </FormField>
                    <button type="submit" disabled={isSubmitting} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-4 font-bold text-white shadow-xl shadow-teal-700/20 transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-70">
                      {isSubmitting ? 'Envoi en cours...' : 'Recevoir une proposition de RDV'}
                      <ArrowRightIcon className="h-5 w-5" />
                    </button>
                    <p className="text-center text-xs leading-5 text-gray-500">
                      En cliquant, vous recevez un message WhatsApp avec 2 ou 3 créneaux possibles dans les 5 jours qui viennent. Aucun engagement, aucun frais.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </Container>
        </section>

        <section className="border-b border-stone-100 bg-white">
          <Container>
            <div className="grid gap-4 py-8 sm:grid-cols-2 lg:grid-cols-4">
              {trustItems.map(([stat, label], index) => (
                <div key={stat} className={`px-2 lg:px-6 ${index > 0 ? 'lg:border-l lg:border-stone-200' : ''}`}>
                  <div className="text-lg font-bold leading-tight text-gray-950">{stat}</div>
                  <div className="mt-1 text-sm leading-5 text-gray-500">{label}</div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section className="bg-white py-24">
          <Container>
            <div className="grid gap-12 lg:grid-cols-[0.85fr_1.35fr] lg:gap-16">
              <div>
                <SectionIntro eyebrow="La promesse" title={<>Une école où votre enfant peut rester <span className="relative inline-block text-teal-700">15 ans<Squiggle className="text-teal-600/35" /></span></>} centered={false} />
                <div className="rounded-2xl border border-teal-600/15 bg-gradient-to-br from-teal-600/10 to-violet-600/10 p-6">
                  <div className="text-3xl font-extrabold tracking-tight text-teal-700">PS → Bac</div>
                  <p className="mt-2 text-sm leading-6 text-gray-700">Pas de test entre la maternelle et le CP.<br />Pas de changement d'école en 1ère AC.</p>
                </div>
              </div>
              <div className="space-y-6 text-[17px] leading-8 text-gray-600">
                <p>Chez Sanaa, choisir une école n'est pas une décision qu'on refait tous les trois ans. La majorité des enfants qui entrent chez nous en petite section y restent jusqu'au baccalauréat.</p>
                <p>4 campus dédiés, tous à Hay El Oulfa : un pour la maternelle, un pour le primaire, un pour le collège, un pour le lycée. Votre enfant grandit dans le même quartier et retrouve les mêmes repères chaque rentrée.</p>
                <p>C'est cette stabilité qui permet à notre équipe de connaître chaque enfant en profondeur, et de l'accompagner sur la durée.</p>
              </div>
            </div>
          </Container>
        </section>

        <section id="programmes" className="bg-[linear-gradient(180deg,#fff_0%,#FBF7F2_100%)] py-24">
          <Container>
            <SectionIntro
              eyebrow="4 programmes pensés pour le 21e siècle"
              title={<>Quatre programmes que vous ne trouverez <span className="relative inline-block text-teal-700">pas ailleurs<Squiggle className="text-teal-600/35" /></span></>}
            >
              Vous reconnaîtrez les matières classiques. Ce qui différencie Sanaa, c'est ce que nous avons ajouté autour : Neurogames, Lumi, Programme IA et Soft Skills.
            </SectionIntro>
            <div className="grid gap-6 lg:grid-cols-2">
              {programmes.map((programme) => {
                const Icon = programme.icon;
                return (
                  <article key={programme.title} className="group relative overflow-hidden rounded-[18px] border border-gray-100 bg-white p-7 shadow-lg shadow-gray-900/[0.04] transition hover:-translate-y-1 hover:shadow-xl">
                    <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full opacity-60 blur-2xl" style={{ backgroundColor: `${programme.color}20` }} />
                    <div className="relative flex items-start gap-4">
                      <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl" style={{ backgroundColor: `${programme.color}18`, color: programme.color }}>
                        <Icon className="h-7 w-7" />
                      </div>
                      <div>
                        <div className="mb-2 inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${programme.color}16`, color: programme.color }}>
                          {programme.audience}
                        </div>
                        <h3 className="text-2xl font-bold tracking-tight text-gray-950">{programme.title}</h3>
                        <p className="mt-1 text-sm italic text-gray-500">{programme.tagline}</p>
                      </div>
                    </div>
                    <p className="relative mt-5 leading-7 text-gray-600">{programme.text}</p>
                    <div className="relative mt-4 flex flex-wrap gap-2">
                      {programme.chips.map((chip) => (
                        <span key={chip} className="rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">{chip}</span>
                      ))}
                    </div>
                    {programme.safety && (
                      <div className="relative mt-4 rounded-xl border border-dashed border-violet-300 bg-stone-50 p-4">
                        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-violet-700">3 engagements de sécurité</div>
                        <div className="grid gap-2 text-sm text-gray-700">
                          {programme.safety.map((item) => <div key={item}>• {item}</div>)}
                        </div>
                      </div>
                    )}
                    <Link to={programme.link} className="relative mt-6 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: programme.color }}>
                      En savoir plus <ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  </article>
                );
              })}
            </div>
          </Container>
        </section>

        <section id="niveaux" className="bg-white py-24">
          <Container>
            <SectionIntro eyebrow="Programme par niveau" title="Choisissez le niveau de votre enfant">
              Quel que soit l'âge de votre enfant, l'inscription se fait par visite privée sur rendez-vous, sur le campus correspondant à son cycle.
            </SectionIntro>
            <div className="grid gap-6 lg:grid-cols-2">
              {niveaux.map((niveau) => (
                <article key={niveau.value} className="group flex h-full flex-col overflow-hidden rounded-[18px] border border-gray-100 bg-white shadow-lg shadow-gray-900/[0.05] transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={`${ASSET_BASE}/${niveau.image}`} alt={`Cycle ${niveau.title}`} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${niveau.color}E6 0%, ${niveau.color}66 36%, transparent 72%)` }} />
                    <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-2 text-xl shadow-lg">{niveau.icon}</div>
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      <h3 className="text-3xl font-bold tracking-tight drop-shadow">{niveau.title}</h3>
                      <p className="mt-1 text-sm font-semibold">{niveau.classes}</p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex-1 leading-7 text-gray-600">{niveau.text}</p>
                    <button type="button" onClick={() => selectNiveau(niveau.value)} className="mt-6 inline-flex w-full items-center justify-between rounded-xl border-2 bg-white px-5 py-3 font-semibold transition group-hover:text-white" style={{ borderColor: niveau.color, color: niveau.color }}>
                      <span>Visite privée {niveau.title.toLowerCase()}</span>
                      <ArrowRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section id="visite" className="bg-[linear-gradient(180deg,#FBF7F2_0%,#fff_100%)] py-24">
          <Container>
            <SectionIntro eyebrow="Comment se passe la visite" title={<>Une visite calme, sur rendez-vous, <span className="relative inline-block text-teal-700">à votre rythme<Squiggle className="text-teal-600/35" /></span></>} />
            <div className="mx-auto max-w-3xl space-y-5">
              {[
                ['#0D9488', "Chaque visite est différente, adaptée à votre enfant, à son niveau, et à ce que vous souhaitez voir. Elle se déroule sur le campus correspondant à son cycle, sans salle d'attente et sans réunion collective."],
                ['#FBB900', "Vous prenez le temps de poser vos questions à l'équipe présente. Nous prenons le temps d'écouter votre projet pour votre enfant. Pas de pression, pas de discours commercial."],
                ['#007CBA', "Pour le primaire, un test de positionnement peut être proposé sur rendez-vous séparé, sans engagement, pour aider à orienter votre enfant vers le bon niveau."],
              ].map(([color, text]) => (
                <div key={text} className="rounded-2xl border border-gray-100 bg-white p-7 shadow-lg shadow-gray-900/[0.04]" style={{ borderLeft: `4px solid ${color}` }}>
                  <p className="text-[17px] leading-8 text-gray-700">{text}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section id="faq" className="bg-white py-24">
          <Container>
            <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
              <div>
                <SectionIntro eyebrow="Foire aux questions" title={<>Vos questions <span className="relative inline-block text-teal-700">avant la visite<Squiggle className="text-teal-600/35" /></span></>} centered={false}>
                  Une question qui n'est pas listée ? Posez-la directement par WhatsApp.
                </SectionIntro>
                <a href="https://wa.me/212781570080" onClick={() => trackOutbound('whatsapp-faq')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-green-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-green-700/15">
                  Poser une question
                </a>
              </div>
              <div className="space-y-3">
                {faqs.map(([question, answer], index) => {
                  const open = openFaq === index;
                  return (
                    <div key={question} className={`overflow-hidden rounded-2xl border transition ${open ? 'border-teal-600/30 bg-teal-50/30' : 'border-gray-100 bg-white'}`}>
                      <button type="button" onClick={() => setOpenFaq(open ? -1 : index)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
                        <span className="font-semibold text-gray-950">{question}</span>
                        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition ${open ? 'rotate-45 bg-teal-600 text-white' : 'bg-teal-600/10 text-teal-700'}`}>+</span>
                      </button>
                      <div className={`grid transition-all ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                        <p className="overflow-hidden px-5 pb-5 leading-7 text-gray-600">{answer}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>

        <section className="relative overflow-hidden bg-[linear-gradient(135deg,#0D9488_0%,#0B7C72_60%,#134E48_100%)] py-24">
          <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-amber-400/25 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-violet-500/25 blur-3xl" />
          <Container>
            <div className="relative mx-auto max-w-4xl text-center text-white">
              <h2 className="text-[clamp(30px,4vw,46px)] font-bold leading-tight tracking-tight">
                Et si l'école qui vous accompagnera 15 ans,{' '}
                <span className="relative inline-block text-amber-300">c'était celle-ci ?<Squiggle className="text-amber-300/45" /></span>
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/85">
                La rentrée 2026-2027 se prépare maintenant. Réservez votre créneau, la suite, on en parle ensemble.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <button type="button" onClick={() => { trackCta('final-visite-privee'); scrollToForm(); }} className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-7 py-4 font-bold text-gray-950 shadow-xl shadow-amber-600/20">
                  Demander une visite privée
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
                <Link to="/portes-ouvertes" className="inline-flex items-center rounded-xl border-2 border-white/35 px-7 py-4 font-semibold text-white transition hover:border-white hover:bg-white/10">
                  Visiter pendant les Portes Ouvertes (16 mai)
                </Link>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-gray-100 bg-white/95 p-3 shadow-2xl backdrop-blur transition lg:hidden ${stickyVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <button type="button" onClick={() => { trackCta('sticky-visite-privee'); scrollToForm(); }} className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-4 font-bold text-white">
          Demander une visite privée
          <ArrowRightIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const FormField = ({
  id,
  label,
  children,
  error,
  helper,
  required,
  optional,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  error?: string;
  helper?: string;
  required?: boolean;
  optional?: boolean;
}) => (
  <div>
    <label htmlFor={id} className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-950">
      {label}
      {required && <span className="text-teal-700">*</span>}
      {optional && <span className="text-xs font-normal text-gray-400">(facultatif)</span>}
    </label>
    {children}
    {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : helper ? <p className="mt-2 text-xs text-gray-500">{helper}</p> : null}
  </div>
);

export default InscriptionPage;
